import { Alert, Box, Link, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import {
    OutboundMessagingConfiguration,
    OutboundMessagingRequest,
} from "models/Emissions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import {
    ConfigurationStatusChip,
    ConfigurationTypeChip,
} from "./EmissionsChips";
import EmissionsFormErrorBanner from "./EmissionsFormErrorBanner";
import EmissionsJsonField, {
    EmissionsJsonFieldRef,
    parseJsonText,
    toJsonText,
} from "./EmissionsJsonField";
import ConfigurationStatusDialog from "./ConfigurationStatusDialog";
import { getSaveErrorMessages } from "./emissionsErrors";
import {
    bodyUsesEndpoint,
    useEmissionsConfigurations,
} from "./useEmissionsConfigurations";

type FormState = {
    name: string;
    description: string;
    tokenEndpoint: string;
    endpointUri: string;
    clientIdVaultKey: string;
    clientSecretVaultKey: string;
    maxRetries: string;
    retryIntervalSeconds: string;
    scopes: string;
    extraParams: string;
    isActive: boolean;
};

// AC 3.3. The API only rejects an empty value, so this is the one place a malformed URI is
// caught. http and https are the schemes the dispatcher can post to; anything else, and anything
// without a scheme at all, would fail at send time instead of at save time.
const isValidEndpointUri = (value: string) => {
    let parsed: URL;
    try {
        parsed = new URL(value.trim());
    } catch {
        return false;
    }

    return parsed.protocol === "http:" || parsed.protocol === "https:";
};

const emptyForm: FormState = {
    name: "",
    description: "",
    tokenEndpoint: "",
    endpointUri: "",
    clientIdVaultKey: "",
    clientSecretVaultKey: "",
    maxRetries: "3",
    retryIntervalSeconds: "30",
    scopes: "",
    extraParams: "",
    isActive: true,
};

const toFormState = (endpoint: OutboundMessagingConfiguration): FormState => ({
    name: endpoint.name ?? "",
    description: endpoint.description ?? "",
    tokenEndpoint: endpoint.tokenEndpoint ?? "",
    endpointUri: endpoint.endpointUri ?? "",
    clientIdVaultKey: endpoint.clientIdVaultKey ?? "",
    clientSecretVaultKey: endpoint.clientSecretVaultKey ?? "",
    maxRetries: String(endpoint.maxRetries ?? ""),
    retryIntervalSeconds: String(endpoint.retryIntervalSeconds ?? ""),
    scopes: toJsonText(endpoint.scopes),
    extraParams: toJsonText(endpoint.extraParams),
    isActive: endpoint.isActive,
});

const textOrNull = (value: string) => (value.trim() ? value.trim() : null);

const numberOrUndefined = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const toRequest = (form: FormState): OutboundMessagingRequest => ({
    name: form.name.trim(),
    description: textOrNull(form.description),
    endpointUri: form.endpointUri.trim(),
    tokenEndpoint: textOrNull(form.tokenEndpoint),
    scopes: parseJsonText(form.scopes),
    clientIdVaultKey: textOrNull(form.clientIdVaultKey),
    clientSecretVaultKey: textOrNull(form.clientSecretVaultKey),
    extraParams: parseJsonText(form.extraParams),
    maxRetries: numberOrUndefined(form.maxRetries),
    retryIntervalSeconds: numberOrUndefined(form.retryIntervalSeconds),
    isActive: form.isActive,
});

const sectionLabelSx = {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.palette.gray[700],
};

const EndpointForm: React.FC = observer(() => {
    const { endpointId } = useParams<{ endpointId?: string }>();
    const navigate = useNavigate();
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const canEdit = authStore.canUserEdit("Emissions Configuration");
    const { service, endpoints, bodies, isLoading, hasError, reload } =
        useEmissionsConfigurations();

    const [form, setForm] = useState<FormState>(emptyForm);
    const [saved, setSaved] = useState<OutboundMessagingConfiguration>();
    const [saveErrors, setSaveErrors] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [statusDialog, setStatusDialog] = useState<"disable" | "enable">();
    const [isChangingStatus, setIsChangingStatus] = useState(false);
    const [jsonValidity, setJsonValidity] = useState({
        scopes: true,
        extraParams: true,
    });
    const scopesRef = useRef<EmissionsJsonFieldRef>(null);
    const extraParamsRef = useRef<EmissionsJsonFieldRef>(null);
    const loadedIdRef = useRef<string>();

    const isNew = !endpointId;
    const loadedEndpoint = useMemo(
        () =>
            endpoints.find(
                (endpoint) =>
                    endpoint.id.toLowerCase() === endpointId?.toLowerCase(),
            ),
        [endpoints, endpointId],
    );
    const endpoint = saved ?? loadedEndpoint;

    useEffect(() => {
        if (loadedEndpoint && loadedIdRef.current !== loadedEndpoint.id) {
            loadedIdRef.current = loadedEndpoint.id;
            setForm(toFormState(loadedEndpoint));
        }
    }, [loadedEndpoint]);

    const usedBy = useMemo(
        () =>
            endpointId
                ? bodies.filter((body) => bodyUsesEndpoint(body, endpointId))
                : [],
        [bodies, endpointId],
    );

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((current) => ({ ...current, [key]: value }));

    const goToConfigurations = () => navigate("../..", { relative: "path" });

    const endpointUriValid =
        !form.endpointUri.trim() || isValidEndpointUri(form.endpointUri);
    // Optional, but a token endpoint that is supplied has to be one a token can be fetched from.
    const tokenEndpointValid =
        !form.tokenEndpoint.trim() || isValidEndpointUri(form.tokenEndpoint);
    const hasRequiredFields =
        !!form.name.trim() &&
        !!form.endpointUri.trim() &&
        isValidEndpointUri(form.endpointUri) &&
        tokenEndpointValid;
    const jsonIsValid = jsonValidity.scopes && jsonValidity.extraParams;

    const activeUsedByNames = usedBy
        .filter((body) => body.isActive)
        .map((body) => body.name);

    const savedForm = useMemo(
        () => (endpoint ? toFormState(endpoint) : undefined),
        [endpoint],
    );
    // A status change posts the saved values, so it would silently drop pending edits.
    const isDirty =
        !!savedForm &&
        (Object.keys(savedForm) as (keyof FormState)[]).some(
            (key) => form[key] !== savedForm[key],
        );
    const statusDisabledTooltip = isDirty
        ? "Save or discard your changes first."
        : undefined;

    // Status is changed only through Disable/Enable, which confirm it. An ordinary save carries
    // whatever status the record already has, so editing an endpoint cannot turn it on or off.
    const save = async () => {
        setSaveErrors([]);
        const problems = [
            scopesRef.current?.validate(),
            extraParamsRef.current?.validate(),
        ].filter((problem): problem is string => !!problem);
        if (problems.length) {
            setSaveErrors(problems);
            return;
        }

        setIsSaving(true);
        try {
            const request = toRequest(form);
            if (isNew) {
                const created = await service.createEndpoint(request);
                // The route changes to the new id while this component stays mounted, and the
                // list it would otherwise look the record up in has not been refetched yet.
                setSaved(created);
                setForm(toFormState(created));
                // The create response has already initialized this record. Do not let the list
                // reload replace the form with differently ordered JSON while the saved baseline
                // still comes from this response, making an untouched form dirty.
                loadedIdRef.current = created.id;
                messageStore.logMessage("Endpoint created.", "success");
                reload();
                navigate(`../${created.id}`, {
                    relative: "path",
                    replace: true,
                });
            } else {
                const updated = await service.updateEndpoint(
                    endpointId,
                    request,
                );
                setSaved(updated);
                setForm(toFormState(updated));
                messageStore.logMessage("Changes saved.", "success");
                if (endpoint?.isActive !== updated.isActive) reload();
            }
        } catch (error) {
            setSaveErrors(await getSaveErrorMessages(error));
        } finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (isActive: boolean) => {
        if (!endpoint || !endpointId) return;
        setSaveErrors([]);
        setIsChangingStatus(true);
        try {
            const updated = await service.setEndpointStatus(
                endpointId,
                isActive,
            );
            setSaved(updated);
            // Keep the displayed JSON and the saved baseline in the same serialization order.
            // Carrying the old text over would leave an untouched form reading as dirty, which
            // disables the very button that just ran.
            setForm(toFormState(updated));
            messageStore.logMessage(
                isActive ? "Endpoint enabled." : "Endpoint disabled.",
                "success",
            );
            reload();
        } catch (error) {
            setSaveErrors(await getSaveErrorMessages(error));
        } finally {
            setIsChangingStatus(false);
            setStatusDialog(undefined);
        }
    };

    const confirmStatusChange = () => {
        if (!statusDialog) return;
        changeStatus(statusDialog === "enable");
    };

    if (isLoading) {
        return <AcxLoadingIndicator alternate="PuffLoader" size={64} />;
    }

    const title = isNew ? "New endpoint" : endpoint?.name ?? "Endpoint";

    return (
        <Box
            sx={{
                padding: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1200px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <OrganizationBreadcrumbs
                additionalBreadcrumbs={[
                    { label: "Emissions", onClick: goToConfigurations },
                    { label: title },
                ]}
            />

            {hasError && (
                <Alert
                    severity="error"
                    action={<AcxButton onClick={reload}>Retry</AcxButton>}
                >
                    Couldn’t load this endpoint.
                </Alert>
            )}

            {!hasError && !isNew && !endpoint && (
                <Alert severity="warning">
                    This endpoint wasn’t found.{" "}
                    <Link component="button" onClick={goToConfigurations}>
                        Back to configurations
                    </Link>
                </Alert>
            )}

            {!hasError && (isNew || endpoint) && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "22px",
                                fontWeight: 700,
                                color: theme.palette.gray[700],
                            }}
                        >
                            {title}
                        </Typography>
                        <ConfigurationTypeChip type="endpoint" />
                        {endpoint && (
                            <ConfigurationStatusChip
                                isActive={endpoint.isActive}
                            />
                        )}
                        {canEdit && (
                            <Box
                                sx={{
                                    marginLeft: "auto",
                                    display: "flex",
                                    gap: "10px",
                                }}
                            >
                                {endpoint?.isActive && (
                                    <AcxButton
                                        variant="outlined"
                                        color="error"
                                        disabled={isSaving || isDirty}
                                        disabledTooltip={
                                            statusDisabledTooltip
                                        }
                                        onClick={() =>
                                            setStatusDialog("disable")
                                        }
                                    >
                                        Disable
                                    </AcxButton>
                                )}
                                {endpoint && !endpoint.isActive && (
                                    <AcxButton
                                        variant="outlined"
                                        color="primary"
                                        loading={isChangingStatus}
                                        disabled={isSaving || isDirty}
                                        disabledTooltip={
                                            statusDisabledTooltip
                                        }
                                        onClick={() =>
                                            setStatusDialog("enable")
                                        }
                                    >
                                        Enable
                                    </AcxButton>
                                )}
                                <AcxButton
                                    variant="contained"
                                    color="primary"
                                    loading={isSaving}
                                    disabled={
                                        !hasRequiredFields ||
                                        !jsonIsValid ||
                                        isChangingStatus
                                    }
                                    onClick={() => save()}
                                >
                                    Save changes
                                </AcxButton>
                            </Box>
                        )}
                    </Box>

                    <EmissionsFormErrorBanner messages={saveErrors} />

                    {endpoint && statusDialog && (
                        <ConfigurationStatusDialog
                            isOpen
                            action={statusDialog}
                            type="endpoint"
                            name={endpoint.name}
                            affectedBodyNames={activeUsedByNames}
                            isLoading={isChangingStatus}
                            onCancel={() => setStatusDialog(undefined)}
                            onConfirm={confirmStatusChange}
                        />
                    )}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "28px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "14px",
                            }}
                        >
                            <AcxMainTextField
                                id="emissions-endpoint-name"
                                labelText="Name"
                                required
                                value={form.name}
                                onChange={(evt) =>
                                    update("name", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-endpoint-description"
                                labelText="Description"
                                value={form.description}
                                onChange={(evt) =>
                                    update("description", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-endpoint-token-endpoint"
                                labelText="Token Endpoint"
                                error={!tokenEndpointValid}
                                errorHelper="Enter a URL starting with http:// or https://"
                                value={form.tokenEndpoint}
                                onChange={(evt) =>
                                    update("tokenEndpoint", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-endpoint-uri"
                                labelText="Endpoint URI"
                                required
                                error={!endpointUriValid}
                                errorHelper="Enter a URL starting with http:// or https://"
                                value={form.endpointUri}
                                onChange={(evt) =>
                                    update("endpointUri", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-endpoint-client-id-vault-key"
                                labelText="ClientIdVaultKey"
                                value={form.clientIdVaultKey}
                                onChange={(evt) =>
                                    update("clientIdVaultKey", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                showCopyButton
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-endpoint-client-secret-vault-key"
                                labelText="ClientSecretVaultKey"
                                value={form.clientSecretVaultKey}
                                onChange={(evt) =>
                                    update(
                                        "clientSecretVaultKey",
                                        evt.target.value,
                                    )
                                }
                                isDisabled={!canEdit}
                                showCopyButton
                                fullWidth
                            />
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                }}
                            >
                                <AcxMainTextField
                                    id="emissions-endpoint-max-retries"
                                    labelText="Max Retries"
                                    type="number"
                                    insertBlankIfNumber
                                    value={form.maxRetries}
                                    onChange={(evt) =>
                                        update("maxRetries", evt.target.value)
                                    }
                                    isDisabled={!canEdit}
                                    fullWidth
                                />
                                <AcxMainTextField
                                    id="emissions-endpoint-retry-interval"
                                    labelText="Retry Interval Seconds"
                                    type="number"
                                    insertBlankIfNumber
                                    value={form.retryIntervalSeconds}
                                    onChange={(evt) =>
                                        update(
                                            "retryIntervalSeconds",
                                            evt.target.value,
                                        )
                                    }
                                    isDisabled={!canEdit}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "14px",
                            }}
                        >
                            <EmissionsJsonField
                                ref={scopesRef}
                                id="emissions-endpoint-scopes"
                                label="Scopes"
                                value={form.scopes}
                                onChange={(value) => update("scopes", value)}
                                onValidityChange={(isValid) =>
                                    setJsonValidity((current) => ({
                                        ...current,
                                        scopes: isValid,
                                    }))
                                }
                                readonly={!canEdit}
                                rowsMax={8}
                            />
                            <EmissionsJsonField
                                ref={extraParamsRef}
                                id="emissions-endpoint-extra-params"
                                label="Extra Params"
                                value={form.extraParams}
                                onChange={(value) =>
                                    update("extraParams", value)
                                }
                                onValidityChange={(isValid) =>
                                    setJsonValidity((current) => ({
                                        ...current,
                                        extraParams: isValid,
                                    }))
                                }
                                readonly={!canEdit}
                                rowsMax={8}
                            />
                            {!isNew && endpointId && (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography sx={sectionLabelSx}>
                                            Used by emission bodies
                                        </Typography>
                                        {canEdit && (
                                            <Link
                                                component="button"
                                                underline="hover"
                                                sx={{ fontSize: "12px" }}
                                                onClick={() =>
                                                    navigate(
                                                        `../../bodies/new?endpointId=${endpointId}`,
                                                        { relative: "path" },
                                                    )
                                                }
                                            >
                                                + New emission body
                                            </Link>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {usedBy.map((body) => (
                                            <Box
                                                key={body.id}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                    padding: "10px 12px",
                                                    borderBottom: `1px solid ${theme.palette.gray[100]}`,
                                                    "&:last-of-type": {
                                                        borderBottom: "none",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Link
                                                        component="button"
                                                        underline="hover"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "13px",
                                                            textAlign: "left",
                                                        }}
                                                        onClick={() =>
                                                            navigate(
                                                                `../../bodies/${body.id}`,
                                                                {
                                                                    relative:
                                                                        "path",
                                                                },
                                                            )
                                                        }
                                                    >
                                                        {body.name}
                                                    </Link>
                                                    {body.description && (
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    "12px",
                                                                color: theme
                                                                    .palette
                                                                    .gray[400],
                                                            }}
                                                        >
                                                            {body.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <ConfigurationStatusChip
                                                    isActive={body.isActive}
                                                />
                                            </Box>
                                        ))}
                                        {!usedBy.length && (
                                            <Typography
                                                sx={{
                                                    padding: "10px 12px",
                                                    fontSize: "13px",
                                                    color: theme.palette
                                                        .gray[400],
                                                }}
                                            >
                                                No emission bodies use this
                                                endpoint yet.
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
});

export default EndpointForm;
